import {
  Title,
  TextInput,
  Button,
  Table,
  ActionIcon,
  Flex,
  Tooltip,
  Group,
  ThemeIcon,
  Text,
  Box,
  Stack,
  Divider,
  Grid,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getProduct,
  changeAlias,
  createAddDeviceSubmission,
  createRemoveDeviceSubmission,
} from "@utils/api-communication/RestApiCli";
import { formatDateAndTime, getVersionStatus } from "@utils/CommonUtil";
import { IconAlertCircle, IconCheck, IconInfoCircle } from "@tabler/icons-react";
import { navigate } from "@utils/navigationService";
import { useDisclosure } from "@mantine/hooks";
import CreateSubmissionModal from "./CreateSubmissionModal";
import { SubmissionType } from "types/userTypes";
import { ProductRequest } from "types/productTypes";
import TestCountView from "@components/common/TestCountView";
import { useDispatch, useSelector } from "react-redux";
import { requestSingleDeviceTestCounts } from "@store/analytics/analyticsSlice";
import { selectSingleDeviceTestCounts } from "@store/analytics/analyticsSelectors";
import { selectUserRole } from "@utils/redux-utils/selectors/userSelectors";

const useStyles = createStyles((_theme) => ({
  table: {
    "& td:nth-child(2)": {
      background: "transparent",
    },
    cursor: "pointer",
  },
}));

export default function OrganizationDeviceList(props) {
  const dispatch = useDispatch();
  const userRole = useSelector(selectUserRole);
  const [organization, setOrganization] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductUuid, setSelectedProductUuid] = useState<string>(null);
  const [aliasEdit, setAliasEdit] = useState(null);
  const [alias, setAlias] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [appUpdateRequired, setAppUpdateRequired] = useState(null);

  const deviceTestCounts = useSelector(selectSingleDeviceTestCounts);
  const { t } = useTranslation();
  const { classes } = useStyles();
  const canManageDeviceSubmissions =
    props.isDist || userRole === "admin" || userRole === "distributor";

  const [isAddModalOpened, addModalHandlers] = useDisclosure(false);
  const [isRemoveModalOpened, removeModalHandlers] = useDisclosure(false);

  useEffect(() => {
    setOrganization(props.organization);
  }, [props, props.organization]);

  useEffect(() => {
    setProducts(organization?.products);
    if (organization?.products?.length > 0) {
      const item = organization.products[0];
      getProductByUuid(item.hardwareModel + "-" + item.serialNo);
    }
  }, [organization]);

  const getProductByUuid = (uuid) => {
    getProduct(
      uuid,
      (response) => {
        setSelectedProduct(response.data);
        getVersionStatus(response.data.version, uuid).then(({ appUpdateReq }) => {
          setAppUpdateRequired(appUpdateReq);
        });
      },
      () => console.error("product does not exist"),
    );
  };

  useEffect(() => {
    setOrganization(props.organization);
  }, [props, props.organization]);

  useEffect(() => {
    setProducts(organization?.products);
    if (organization?.products?.length > 0) {
      const item = organization.products[0];
      setSelectedProductUuid(item.hardwareModel + "-" + item.serialNo);
    }
  }, [organization]);

  useEffect(() => {
    if (selectedProductUuid && organization?.orgId) {
      getProductByUuid(selectedProductUuid);
      dispatch(
        requestSingleDeviceTestCounts({ deviceId: selectedProductUuid, orgId: organization.orgId }),
      );
    }
  }, [selectedProductUuid, organization?.orgId, dispatch]);

  const getUpdateInfoAndAction = (product) => {
    return (
      <Flex justify={"space-between"} align={"center"}>
        {appUpdateRequired ? (
          <Group gap={"xs"} wrap="nowrap">
            <ThemeIcon color="orange" size="sm">
              <IconAlertCircle />
            </ThemeIcon>
            <Text c="dimmed" fz={"sm"}>
              {t("DeviceCard.newUpdateAvailable")}
            </Text>
          </Group>
        ) : (
          <Group gap={"xs"} wrap="nowrap">
            <ThemeIcon color="gray" size="sm" variant="light">
              <IconCheck />
            </ThemeIcon>
            <Text c="dimmed" fz={"sm"}>
              {t("VersionControl.noUpdateInfo")}
            </Text>
          </Group>
        )}
        <Button
          onClick={() => navigate("/version-control?pid=" + product.uuid)}
          color={appUpdateRequired ? "orange" : "gray"}
          size="compact-md"
        >
          {appUpdateRequired ? t("DeviceCard.updatePage") : t("DevicePage")}
        </Button>
      </Flex>
    );
  };

  const handleAliasChange = (uuid, alias) => {
    changeAlias(
      props.isDist,
      { orgId: organization.orgId, uuid: uuid, alias: alias },
      () => {
        setAliasEdit(null);
        setAlias("");
        const productsNew = products.map((item) => {
          if (item.hardwareModel + "-" + item.serialNo === uuid) {
            console.log("found");
            item.alias = alias;
          }
          return item;
        });
        console.log(productsNew);
        setProducts(productsNew);
      },
      () => {
        setAliasEdit(null);
        setAlias("");
      },
    );
  };

  const handleDeviceSubmissionRequest = async (formData: ProductRequest) => {
    return new Promise((resolve, reject) => {
      createAddDeviceSubmission(
        {
          targetOrgId: organization.orgId,
          hardwareModel: formData.hardwareModel,
          serialNo: formData.serialNo,
          alias: formData.alias,
        },
        (response) => {
          console.log("Device submission requested:", response);
          resolve(response);
        },
        (error) => {
          console.error("Failed to create device submission:", error);
          reject(new Error(error?.message || t("OrganizationSettings.errorAddingDevice")));
        },
      );
    });
  };

  const selectedProductItem =
    products?.find((item) => item.hardwareModel + "-" + item.serialNo === selectedProductUuid) ||
    null;

  const handleRemoveDeviceSubmissionRequest = async (formData: ProductRequest) => {
    return new Promise((resolve, reject) => {
      createRemoveDeviceSubmission(
        {
          targetOrgId: organization.orgId,
          hardwareModel: formData.hardwareModel,
          serialNo: formData.serialNo,
          alias: formData.alias,
        },
        (response) => {
          console.log("Device removal submission requested:", response);
          resolve(response);
        },
        (error) => {
          console.error("Failed to create device removal submission:", error);
          reject(new Error(error?.message || t("OrganizationSettings.errorRemovingDevice")));
        },
      );
    });
  };

  return (
    <Stack p={"md"} gap={"xl"} style={{ width: "100%" }}>
      <Box>
        <Group justify="space-between" align="center">
          <Group>
            <Title size={"20px"}>{t("OrganizationSettings.devices")}</Title>
            <Text>({organization?.orgId})</Text>
          </Group>
          {canManageDeviceSubmissions ? (
            <>
              <Group gap="sm">
                <Button
                  color="red"
                  variant="light"
                  onClick={removeModalHandlers.open}
                  disabled={!selectedProductItem}
                >
                  {t("OrganizationSettings.buttonRemoveDevice")}
                </Button>
                <Button onClick={addModalHandlers.open}>
                  {t("OrganizationSettings.buttonAddNewDevice")}
                </Button>
              </Group>

              <CreateSubmissionModal
                opened={isRemoveModalOpened}
                onClose={removeModalHandlers.close}
                title={t("submissions.removeDeviceSubmissionRequestModal.title")}
                description={t("submissions.removeDeviceSubmissionRequestModal.description", {
                  deviceId: selectedProductUuid || t("common.notAvailable"),
                  organizationName: organization?.name || t("common.thisOrganization"),
                })}
                submissionType={SubmissionType.REMOVE_DEVICE}
                onSubmit={handleRemoveDeviceSubmissionRequest}
                targetOrgId={organization?.orgId}
                initialRequest={
                  selectedProductItem
                    ? {
                        hardwareModel: selectedProductItem.hardwareModel,
                        serialNo: selectedProductItem.serialNo,
                        alias: selectedProductItem.alias || "",
                      }
                    : null
                }
              />

              <CreateSubmissionModal
                opened={isAddModalOpened}
                onClose={addModalHandlers.close}
                title={t("submissions.newDeviceSubmissionRequestModal.title")}
                description={t("submissions.newDeviceSubmissionRequestModal.description", {
                  organizationName: organization?.name || t("common.thisOrganization"),
                })}
                submissionType={SubmissionType.ADD_DEVICE}
                onSubmit={handleDeviceSubmissionRequest}
                targetOrgId={organization?.orgId}
              />
            </>
          ) : null}
        </Group>

        <Table highlightOnHover className={classes.table}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Alias</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {products?.map((item, index) => {
              const productId = item.hardwareModel + "-" + item.serialNo;
              const isSelected = productId === selectedProductUuid;
              return (
                <Table.Tr
                  key={index}
                  onClick={() => setSelectedProductUuid(item.hardwareModel + "-" + item.serialNo)}
                  style={{
                    backgroundColor: isSelected ? "var(--mantine-color-blue-0)" : undefined,
                    borderLeft: isSelected
                      ? "4px solid var(--mantine-color-blue-6)"
                      : "4px solid transparent",
                  }}
                >
                  <Table.Td>
                    <Flex justify={"flex-start"} align={"center"}>
                      <Tooltip label={t("DevicePage")}>
                        <ActionIcon
                          mr={"sm"}
                          size="md"
                          variant="subtle"
                          color={"blue"}
                          onClick={() =>
                            navigate(
                              "/version-control?pid=" + item.hardwareModel + "-" + item.serialNo,
                            )
                          }
                        >
                          <IconInfoCircle />
                        </ActionIcon>
                      </Tooltip>
                      {item.hardwareModel + "-" + item.serialNo}
                    </Flex>
                  </Table.Td>

                  <Table.Td>
                    {aliasEdit === index ? (
                      <TextInput
                        name="alias"
                        value={alias}
                        onChange={(event) => setAlias(event.target.value)}
                      />
                    ) : (
                      item.alias
                    )}
                  </Table.Td>
                  <Table.Td>
                    {aliasEdit === index ? (
                      <Button.Group>
                        <Button
                          variant="outline"
                          color="green"
                          onClick={() => {
                            handleAliasChange(item.hardwareModel + "-" + item.serialNo, alias);
                          }}
                          size="compact-md"
                        >
                          {t("OrganizationSettings.submit")}
                        </Button>
                        <Button
                          variant="outline"
                          color="red"
                          onClick={() => setAliasEdit(null)}
                          size="compact-md"
                        >
                          {t("OrganizationSettings.cancel")}
                        </Button>
                      </Button.Group>
                    ) : (
                      <Group gap="xs">
                        <Button
                          variant="outline"
                          onClick={() => setAliasEdit(index)}
                          size="compact-md"
                        >
                          {t("OrganizationSettings.edit")}
                        </Button>
                        <Tooltip label={t("DevicePage")}>
                          <ActionIcon
                            size="md"
                            variant="subtle"
                            color="blue"
                            aria-label={t("DevicePage")}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                "/version-control?pid=" + item.hardwareModel + "-" + item.serialNo,
                              );
                            }}
                          >
                            <IconInfoCircle />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>
      {selectedProduct ? (
        <>
          <Divider />
          <Grid gutter={"lg"}>
            <Grid.Col span={{ base: 12, sm: 12, xs: 12, md: 6 }}>
              <Title size={"20px"}>{selectedProduct?.uuid}</Title>
              <Table>
                <Table.Tbody>
                  {selectedProduct?.alias ? (
                    <Table.Tr>
                      <Table.Td> Alias</Table.Td>
                      <Table.Td>{selectedProduct?.alias}</Table.Td>
                    </Table.Tr>
                  ) : null}
                  <Table.Tr>
                    <Table.Td> Version</Table.Td>
                    <Table.Td>{selectedProduct?.version}</Table.Td>
                  </Table.Tr>
                  {selectedProduct?.uuid?.startsWith("pn3") ||
                  selectedProduct?.uuid?.startsWith("p4e") ||
                  selectedProduct?.uuid?.startsWith("psw") ? (
                    <>
                      <Table.Tr>
                        <Table.Td> Update Status</Table.Td>
                        <Table.Td>
                          {selectedProduct.version && selectedProduct.puiVersion ? (
                            getUpdateInfoAndAction(selectedProduct)
                          ) : (
                            <p style={{ color: "grey" }}>
                              {t("OrganizationSettings.noVersionInfo")}
                            </p>
                          )}
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td> OS Version</Table.Td>
                        <Table.Td>{selectedProduct?.puiVersion}</Table.Td>
                      </Table.Tr>
                    </>
                  ) : null}
                  <Table.Tr>
                    <Table.Td>VR Headset Battery</Table.Td>
                    <Table.Td>{selectedProduct?.battery}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>Last Access</Table.Td>
                    <Table.Td>{formatDateAndTime(selectedProduct?.lastAccess)}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>Last Access from VR</Table.Td>
                    <Table.Td>{formatDateAndTime(selectedProduct?.vrDate)}</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>Last Access from the panel</Table.Td>
                    <Table.Td>{formatDateAndTime(selectedProduct?.webDate)}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 12, xs: 12, md: 6 }}>
              <TestCountView analytics={deviceTestCounts} />
            </Grid.Col>
          </Grid>
        </>
      ) : null}
    </Stack>
  );
}
